import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Icon } from '../components/Icon.tsx';
import { requestsApi, type RequestStatus } from '../features/requests/api.ts';
import { t } from '../lib/strings.ts';
import { formatDate } from '@/lib/format';
import { Pagination } from '../components/Pagination.tsx';

const PAGE_SIZE = 10;

const STATUS_LABELS: Record<RequestStatus, string> = {
  DRAFT: t.requests.statusDraft,
  SENT: t.requests.statusSent,
  CLOSED: t.requests.statusClosed,
};

function statusPill(status: RequestStatus): string {
  switch (status) {
    case 'DRAFT': return 'pill pill-neutral';
    case 'SENT': return 'pill pill-pending';
    case 'CLOSED': return 'pill pill-success';
  }
}

type StatusFilter = 'all' | RequestStatus;

export function Requests(): JSX.Element {
  const { data, isLoading } = useQuery({
    queryKey: ['requests'],
    queryFn: () => requestsApi.list({ page: 1, pageSize: 1000 }),
  });
  const requests = useMemo(() => data?.requests ?? [], [data]);

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  // Title/ref search, debounced so we don't filter per keystroke.
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search.trim().toLowerCase()), 300);
    return () => clearTimeout(id);
  }, [search]);

  // Page resets whenever a filter/search changes (the result set changes).
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [statusFilter, debouncedSearch]);

  const filtered = useMemo(() => requests.filter((r) => {
    if (statusFilter !== 'all' && r.status !== statusFilter) return false;
    if (debouncedSearch) {
      const hay = `${r.title} ${r.refNumber}`.toLowerCase();
      if (!hay.includes(debouncedSearch)) return false;
    }
    return true;
  }), [requests, statusFilter, debouncedSearch]);

  const total = filtered.length;
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const statusTabs: { key: StatusFilter; label: string }[] = [
    { key: 'all', label: t.requests.filterAll },
    { key: 'DRAFT', label: t.requests.statusDraft },
    { key: 'SENT', label: t.requests.statusSent },
    { key: 'CLOSED', label: t.requests.statusClosed },
  ];

  return (
    <div className="page space-y-6">
      <header className="flex items-center justify-between">
        <h1 className="font-serif text-3xl tracking-tight">{t.requests.title}</h1>
        <Link to="/gsr/new" className="btn-primary">{t.requests.newRequest}</Link>
      </header>

      {/* Toolbar — M3 segmented status filter on one side, search on the other,
          on the tinted chrome ground like WapMeet's meetings list. */}
      <div className="rounded-3xl bg-[hsl(var(--chrome))] flex flex-wrap items-center justify-between gap-x-6 gap-y-3 p-3 sm:px-4">
        <div className="flex flex-wrap items-center gap-2">
          <div
            className="inline-flex items-center overflow-hidden rounded-full border border-[hsl(var(--border))]"
            role="group"
            aria-label={t.requests.title}
          >
            {statusTabs.map((tab, i) => {
              const active = statusFilter === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setStatusFilter(tab.key)}
                  className={`inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium transition-colors ${
                    i > 0 ? 'border-s border-[hsl(var(--border))]' : ''
                  } ${
                    active
                      ? 'bg-[hsl(var(--secondary-container))] text-[hsl(var(--on-secondary-container))]'
                      : 'text-foreground hover:bg-black/[0.04] dark:hover:bg-white/[0.06]'
                  }`}
                >
                  {active && <Icon name="check" size={16} />}
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
        <div className="relative w-full sm:w-72">
          <Icon name="search" size={18} className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t.requests.searchPlaceholder}
            className="input rounded-full py-1.5 ps-9 pe-4"
          />
        </div>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground" role="status" aria-live="polite">
          {t.requests.loading}
        </p>
      ) : (
        <section className="rounded-3xl bg-[hsl(var(--chrome))] divide-y divide-black/[0.05] overflow-hidden">
          {pageItems.map((r) => (
            <Link
              key={r.id}
              to="/requests/$requestId"
              params={{ requestId: r.id }}
              className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 p-4 transition-colors hover:bg-[hsl(var(--surface-high))] sm:px-5"
            >
              <div className="min-w-0">
                <p className="flex flex-wrap items-center gap-x-2 gap-y-1 font-medium leading-snug">
                  <span>{r.title}</span>
                </p>
                <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-1.5">
                    <Icon name="description" size={15} className="shrink-0 text-foreground/40" />
                    <span className="num">{r.refNumber}</span>
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <Icon name="checklist" size={15} className="shrink-0 text-foreground/40" />
                    <span className="num">{r.items.length}</span>
                  </span>
                  {r.deadline && (
                    <span className="inline-flex items-center gap-1.5">
                      <Icon name="calendar_month" size={15} className="shrink-0 text-foreground/40" />
                      <span className="num">{formatDate(r.deadline)}</span>
                    </span>
                  )}
                </div>
              </div>
              <span className={`${statusPill(r.status)} shrink-0`}>{STATUS_LABELS[r.status]}</span>
            </Link>
          ))}
          {total === 0 && (
            <p className="p-8 text-center text-sm text-muted-foreground">{t.requests.empty}</p>
          )}
        </section>
      )}

      <Pagination page={page} pageSize={PAGE_SIZE} total={total} onChange={setPage} />
    </div>
  );
}
